import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HttpService } from '../../game/services/HttpService';
import './EmailVerification.component.css';

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const codeSentRef = useRef(false); // Use ref to prevent duplicate sends

  const httpService = new HttpService();
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const userEmail = localStorage.getItem('userEmail') || '';

  // Auto-send verification code when component mounts (only once, even in React StrictMode)
  useEffect(() => {
    if (token && userId && !codeSentRef.current) {
      codeSentRef.current = true; // Mark as sent immediately to prevent duplicates
      sendVerificationCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount, not when token/userId changes

  const sendVerificationCode = async () => {
    if (!token || !userId) {
      setError('You must be logged in to verify your email');
      return;
    }

    setSendingCode(true);
    setError('');
    setSuccess('');

    try {
      httpService.setToken(token);
      const response = await httpService.post<any>('user/resendVerificationCode', {
        userId: userId,
      });

      if (response.status === 'success') {
        setCodeSent(true);
        setSuccess('Verification code sent to your email');
      } else {
        setError(response.message || 'Failed to send verification code');
      }
    } catch (error: any) {
      console.error('Error sending verification code:', error);
      setError(error.message || 'Failed to send verification code. Please try again.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (verificationCode.length < 5) {
      setError('Verification code must be at least 5 characters long');
      return;
    }

    if (!token || !userId) {
      setError('You must be logged in to verify your email');
      return;
    }

    setLoading(true);

    try {
      httpService.setToken(token);
      const response = await httpService.post<any>('user/verifyEmail', {
        verificationCode: verificationCode,
        userId: userId,
      });

      if (response.status === 'success') {
        setSuccess('Email verified successfully!');
        // Update emailVerified in localStorage
        localStorage.setItem('emailVerified', 'true');
        // Redirect back to game page after a short delay
        setTimeout(() => {
          const returnTo = searchParams.get('returnTo') || '/game';
          navigate(returnTo);
        }, 1000);
      } else {
        setError(response.message || 'Invalid verification code');
      }
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setError(error.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const returnTo = searchParams.get('returnTo') || '/game';
    navigate(returnTo);
  };

  return (
    <div className="email-verification-container">
      <div className="email-verification-background"></div>
      <div className="email-verification-content">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="email-verification-card"
        >
          <h2>Email Verification</h2>
          <p className="email-verification-hint">
            {codeSent
              ? `A verification code has been sent to ${userEmail || 'your email'}. Please enter the code below.`
              : sendingCode
              ? 'Sending verification code...'
              : 'Please verify your email to continue playing.'}
          </p>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="email-verification-error"
            >
              {error}
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="email-verification-success"
            >
              {success}
            </motion.div>
          )}

          <form onSubmit={handleVerifyCode} className="email-verification-form">
            <div className="form-group">
              <label htmlFor="verificationCode">Verification Code</label>
              <input
                id="verificationCode"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter verification code"
                disabled={loading || sendingCode}
                maxLength={10}
                autoFocus
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleCancel}
                className="cancel-button"
                disabled={loading || sendingCode}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="verify-button"
                disabled={loading || sendingCode || verificationCode.length < 5}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>

            {codeSent && (
              <button
                type="button"
                onClick={sendVerificationCode}
                className="resend-button"
                disabled={sendingCode}
              >
                {sendingCode ? 'Sending...' : 'Resend Code'}
              </button>
            )}
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default EmailVerification;

